import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import Image from '@mui/icons-material/Image';
import LayersIcon from '@mui/icons-material/Layers';
import ViewModule from '@mui/icons-material/ViewModule';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import Alert from '@mui/material/Alert';
import { imageShape } from '../shapes/ImageShapes';
import ImageCheckList from './ImageCheckList';
import ImagesTransform from './ImagesTransform';
import Layers from './Layers';
import Partify from './Partify';
import Intensify from './Intensify';
import Slide from './Slide';
import Append from './Append';
import CanvasModifier from './CanvasModifier';
import Emoji from './UI/Emoji';
import { TOOLS, toolData } from '../constants/tool_constants';

import { makeStyles } from 'tss-react/mui';
import Frames from './Frames';

const useStyles = makeStyles()({
  toolDrawerPaper: {
    top: 'var(--header-height)',
    width: 'var(--drawer-width)',
    height: 'calc(100% - var(--header-height))',
    left: 'var(--rail-width)',
    borderColor: '#e1e6de',
    '@media (max-width: 899px)': {
      top: 'auto',
      left: 0,
      bottom: 0,
      // Keep the bottom tool sheet above the permanent navigation rail.
      zIndex: 1201,
      width: '100%',
      height: 'auto',
      maxHeight: 'var(--mobile-tool-height)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      boxSizing: 'border-box',
      borderRadius: '20px 20px 0 0',
    },
  },
  toolBarBottomNavigationRoot: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: 'auto',
    padding: '12px 8px',
    gap: '4px',
    '@media (max-width: 899px)': {
      flexDirection: 'row',
      padding: '4px 8px',
      gap: 0,
      minWidth: 'max-content',
    },
  },
  toolBarPaper: {
    top: 'var(--header-height)',
    width: 'var(--rail-width)',
    height: 'calc(100% - var(--header-height))',
    borderColor: '#e1e6de',
    '@media (max-width: 899px)': {
      top: 'auto',
      bottom: 0,
      width: '100%',
      height: 'auto',
      overflowX: 'auto',
      borderTop: '1px solid #e1e6de',
      paddingBottom: 'env(safe-area-inset-bottom)',
    },
  },
  toolDrawerContentWrapper: {
    padding: '20px',
  },
  imageSelectRoot: {
    textAlign: 'left',
  },
});

const Toolbox = ({
  images,
  imagesAreLoading = false,
  selectedImages = [],
  isOpen,
  onDrawerClose = () => {},
  activeTool = TOOLS.NONE,
  onToolChange = () => {},
  onImageCheckboxChange = () => {},
  onImageSelect = () => {},
  selectedImagesNames = {},
  selectedFrameIndices = [],
  onImageChange = () => {},
  onImageChangeStart = () => {},
  onImageRemoval = () => {},
  onImageReorder = () => {},
  selectedPixel = [],
  eyeDropperIsEnabled = false,
  onEyeDropperClick = () => {},
  onCanvasChange = () => {},
  canvasDimensions = {},
  canvasBackgroundColor = '',
  onFramesChange = () => {},
  firstSelectedImageFrameOrder,
  onFramesPreviewClick = () => {},
  onFramesPreviewBackClick = () => {},
  framesIsPreviewing = false,
  onImageSelectToggle = () => {},
  isImageSelectOpen = false,
  onAddImageClick = () => {},
}) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery('(max-width: 899px)');
  // We use a specific isOpen flag for the error pop-up to avoid showing a blank error
  // for half a second before closing
  const [error, setError] = useState('');
  const [errorIsOpen, setErrorIsOpen] = useState(false);
  const handleCloseError = () => {
    setErrorIsOpen(false);
  };
  const handleError = (message) => {
    setError(message);
    setErrorIsOpen(true);
  };

  return (
    <>
      <Drawer
        anchor={isMobile ? 'bottom' : 'left'}
        // Tools and canvas must remain interactive together, including on mobile.
        variant="persistent"
        open={isOpen}
        onClose={onDrawerClose}
        classes={{ paper: classes.toolDrawerPaper }}
        PaperProps={{ role: 'region', 'aria-label': 'Tool settings' }}
      >
        <div className={`${classes.toolDrawerContentWrapper} glint-animate-entry`} key={activeTool}>
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography component="h2" variant="h6">
                {toolData[activeTool].title}
              </Typography>
              <IconButton onClick={onDrawerClose} aria-label="Close tool panel">
                <ChevronLeft />
              </IconButton>
            </Box>
            <Divider />
            <Box marginBottom="10px" />
            <Snackbar open={errorIsOpen} onClose={handleCloseError}>
              <Alert onClose={handleCloseError} variant="filled" severity="error">
                {error}
              </Alert>
            </Snackbar>
            <div key={activeTool} className="glint-animate-entry">
              {toolData[activeTool].showImageList && (
                <>
                  {toolData[activeTool].canSelectMultiple ? (
                    <ImageCheckList
                      images={images}
                      onCheckboxChange={onImageCheckboxChange}
                      checkedImagesNames={selectedImagesNames}
                    />
                  ) : (
                    <Box display="flex">
                      <Box marginRight="10px">
                        <Image color="action" />
                      </Box>
                      <Box width="100%">
                        <Select
                          classes={{ root: classes.imageSelectRoot }}
                          value={selectedImages[0]?.name ?? ''}
                          onChange={(e) => onImageSelect(e.target.value)}
                          inputProps={{ 'aria-label': 'Select image to edit' }}
                          displayEmpty
                          fullWidth
                          native={false}
                          onOpen={onImageSelectToggle}
                          onClose={onImageSelectToggle}
                          open={isImageSelectOpen}
                        >
                          <MenuItem value="">Choose an image</MenuItem>
                          {images.map((image) => (
                            <MenuItem key={image.name} value={image.name}>
                              {image.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>
                  )}
                  <Box marginBottom="10px" />
                </>
              )}
              {(() => {
                switch (activeTool) {
                  case TOOLS.IMAGES:
                    return (
                      <ImagesTransform
                        images={images}
                        disabled={imagesAreLoading}
                        onImageChange={onImageChange}
                        onImageRemoval={onImageRemoval}
                        onImageChangeStart={onImageChangeStart}
                        onAddImageClick={onAddImageClick}
                      />
                    );
                  case TOOLS.LAYERS:
                    return (
                      <Layers
                        images={images}
                        onImageChange={onImageReorder}
                        onImageChangeStart={onImageChangeStart}
                      />
                    );
                  case TOOLS.FRAMES:
                    return (
                      <Frames
                        image={selectedImages[0] ?? {}}
                        selectedFrameIndices={selectedFrameIndices}
                        frameOrder={firstSelectedImageFrameOrder}
                        onSubmit={onFramesChange}
                        onPreviewClick={onFramesPreviewClick}
                        onPreviewBackClick={onFramesPreviewBackClick}
                        isPreviewing={framesIsPreviewing}
                      />
                    );
                  case TOOLS.PARTIFY:
                    return (
                      <Partify
                        images={selectedImages}
                        onImageChange={onImageChange}
                        onImageChangeStart={onImageChangeStart}
                        onEyeDropperClick={onEyeDropperClick}
                        selectedPixel={selectedPixel}
                        eyeDropperIsEnabled={eyeDropperIsEnabled}
                      />
                    );
                  case TOOLS.INTENSIFY:
                    return (
                      <Intensify
                        images={selectedImages}
                        onImageChange={onImageChange}
                        onImageChangeStart={onImageChangeStart}
                      />
                    );
                  case TOOLS.SLIDE:
                    return (
                      <Slide
                        images={selectedImages}
                        onImageChange={onImageChange}
                        onImageChangeStart={onImageChangeStart}
                        onError={handleError}
                      />
                    );
                  case TOOLS.CANVAS:
                    return (
                      <CanvasModifier
                        onChange={onCanvasChange}
                        width={canvasDimensions.width}
                        height={canvasDimensions.height}
                        backgroundColor={canvasBackgroundColor}
                      />
                    );
                  case TOOLS.APPEND:
                    return (
                      <Append
                        images={selectedImages}
                        onImageChange={onImageChange}
                        onImageChangeStart={onImageChangeStart}
                      />
                    );
                  case TOOLS.NONE:
                  default:
                    return null;
                }
              })()}
            </div>
          </>
        </div>
      </Drawer>
      <Drawer variant="permanent" open anchor="left" classes={{ paper: classes.toolBarPaper }}>
        <BottomNavigation
          component="nav"
          aria-label="Editing tools"
          showLabels
          value={activeTool}
          onChange={(e, value) => {
            onToolChange(value);
          }}
          classes={{ root: classes.toolBarBottomNavigationRoot }}
        >
          <BottomNavigationAction
            className="glint-nav-item"
            label="Images"
            icon={<Image />}
            aria-pressed={activeTool === TOOLS.IMAGES}
            value={TOOLS.IMAGES}
          />
          <BottomNavigationAction
            className="glint-nav-item"
            label="Layers"
            icon={<LayersIcon />}
            aria-pressed={activeTool === TOOLS.LAYERS}
            value={TOOLS.LAYERS}
          />
          <BottomNavigationAction
            className="glint-nav-item"
            label="Frames"
            icon={<ViewModule />}
            aria-pressed={activeTool === TOOLS.FRAMES}
            value={TOOLS.FRAMES}
          />
          <BottomNavigationAction
            className="glint-nav-item"
            label="Partify"
            icon={<Emoji symbol="🎉" label="party" />}
            aria-pressed={activeTool === TOOLS.PARTIFY}
            value={TOOLS.PARTIFY}
          />
          <BottomNavigationAction
            className="glint-nav-item"
            label="Intensify"
            icon={<Emoji symbol="↔️" label="shake" />}
            aria-pressed={activeTool === TOOLS.INTENSIFY}
            value={TOOLS.INTENSIFY}
          />
          <BottomNavigationAction
            className="glint-nav-item"
            label="Slide"
            icon={<Emoji symbol="↩️" label="slide" />}
            aria-pressed={activeTool === TOOLS.SLIDE}
            value={TOOLS.SLIDE}
          />
          <BottomNavigationAction
            className="glint-nav-item"
            label="Append"
            icon={<Emoji symbol="➕" label="append" />}
            aria-pressed={activeTool === TOOLS.APPEND}
            value={TOOLS.APPEND}
          />
          <BottomNavigationAction
            className="glint-nav-item"
            label="Canvas"
            icon={<Emoji symbol="⬜" label="canvas" />}
            aria-pressed={activeTool === TOOLS.CANVAS}
            value={TOOLS.CANVAS}
          />
        </BottomNavigation>
      </Drawer>
    </>
  );
};

Toolbox.propTypes = {
  images: PropTypes.arrayOf(imageShape).isRequired,
  imagesAreLoading: PropTypes.bool,
  selectedImages: PropTypes.arrayOf(imageShape),
  isOpen: PropTypes.bool.isRequired,
  onDrawerClose: PropTypes.func,
  activeTool: PropTypes.string,
  onToolChange: PropTypes.func,
  onImageCheckboxChange: PropTypes.func,
  onImageSelect: PropTypes.func,
  selectedImagesNames: PropTypes.object,
  selectedFrameIndices: PropTypes.arrayOf(PropTypes.number),
  onImageChange: PropTypes.func,
  onImageChangeStart: PropTypes.func,
  onImageRemoval: PropTypes.func,
  onImageReorder: PropTypes.func,
  selectedPixel: PropTypes.objectOf(PropTypes.number),
  eyeDropperIsEnabled: PropTypes.bool,
  onEyeDropperClick: PropTypes.func,
  onCanvasChange: PropTypes.func,
  canvasDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  canvasBackgroundColor: PropTypes.string,
  onFramesChange: PropTypes.func,
  onFramesPreviewClick: PropTypes.func,
  onFramesPreviewBackClick: PropTypes.func,
  framesIsPreviewing: PropTypes.bool,
  onImageSelectToggle: PropTypes.func,
  isImageSelectOpen: PropTypes.bool,
  onAddImageClick: PropTypes.func,
};

export default Toolbox;
